import { observer } from 'mobx-react-lite'
import Box from '@mui/material/Box'
import Button from '@mui/material/Button'
import Chip from '@mui/material/Chip'
import Stack from '@mui/material/Stack'
import Typography from '@mui/material/Typography'
import InfoOutlined from '@mui/icons-material/InfoOutlined'
import { themeConfig } from '@gap/shared/theme'
import GapDialog from 'reusable/gap-dialog/GapDialog'
import { useAdvisorIntegrationFYIStore } from './AdvisorIntegrationFYIStore'
import { AdvisorIntegrationFYIConnectModalContent } from './AdvisorIntegrationFYIKeysForm'
import FYISyncModeOptions, { FYISwitchSourceWarning } from './FYISyncModeOptions'

/** The connect dialog: choose what to sync, then enter the keys; keys only where the two-step flow is off. */
export default observer(function AdvisorIntegrationFYIConnectDialog() {
   const advisorIntegrationFYIStore = useAdvisorIntegrationFYIStore()
   const { usesStepFlow, connectStep, connectDialogTitle } = advisorIntegrationFYIStore

   return <GapDialog
      size="small"
      title={usesStepFlow ? <DialogStepTitle step={connectStep} title={connectDialogTitle} /> : connectDialogTitle}
      subTitle={usesStepFlow ? undefined : 'Store client files directly in FYI'}
      modalState={advisorIntegrationFYIStore.connectDialog}
      cancel={advisorIntegrationFYIStore.showCancelButton}
      secondary={advisorIntegrationFYIStore.secondaryGapDialogButton}
      primary={advisorIntegrationFYIStore.primaryGapDialogButton}
   >
      <ConnectDialogBody />
   </GapDialog>
})

/** Renders the step overline above the dialog title, as spans. */
function DialogStepTitle(props: { step: 1 | 2; title: string }) {
   return <Stack component="span">
      <Typography component="span" variant="overline" sx={{ color: themeConfig.primary[600] }}>Step {props.step} of 2</Typography>
      <Box component="span">{props.title}</Box>
   </Stack>
}

const ConnectDialogBody = observer(function ConnectDialogBody() {
   const advisorIntegrationFYIStore = useAdvisorIntegrationFYIStore()

   if (!advisorIntegrationFYIStore.usesStepFlow) return <AdvisorIntegrationFYIConnectModalContent />
   return advisorIntegrationFYIStore.connectStep === 1 ? <ChooseSyncModeStep /> : <EnterKeysStep />
})

const ChooseSyncModeStep = observer(function ChooseSyncModeStep() {
   const advisorIntegrationFYIStore = useAdvisorIntegrationFYIStore()

   return <>
      <FYISyncModeOptions variant="connect" />
      <Stack direction="row" alignItems="center" spacing={1} sx={{ mt: 2, color: themeConfig.primary[600] }}>
         <InfoOutlined sx={{ fontSize: 18 }} />
         <Typography variant="body2">You can change this later.</Typography>
      </Stack>
      {advisorIntegrationFYIStore.needsSwitchAcknowledgement && <FYISwitchSourceWarning />}
   </>
})

const EnterKeysStep = observer(function EnterKeysStep() {
   const advisorIntegrationFYIStore = useAdvisorIntegrationFYIStore()
   const chips = advisorIntegrationFYIStore.connectSyncModeOptions.find(option => option.value === advisorIntegrationFYIStore.syncMode)?.chips ?? []

   return <>
      <Stack direction="row" alignItems="center" sx={{ gap: 1 }}>
         <Typography variant="body2" sx={{ color: themeConfig.primary[600] }}>You're connecting</Typography>
         {chips.map(chip => <Chip key={chip} label={chip} size="small" variant="outlined" />)}
         <Button variant="text" size="small" onClick={advisorIntegrationFYIStore.goToStepOne} disabled={advisorIntegrationFYIStore.connectLoader.isLoading} sx={{ ml: 'auto' }}>Change</Button>
      </Stack>
      <AdvisorIntegrationFYIConnectModalContent />
      {advisorIntegrationFYIStore.shouldSetClientSource && <Typography variant="caption" sx={{ display: 'block', mt: 2, color: themeConfig.primary[600] }}>
         Your first client sync starts as soon as you connect. You can follow it in Client sync log.
      </Typography>}
   </>
})
